import React, { useRef, useCallback, useEffect, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import {
  Search,
  Users,
  ShieldCheck,
  Shield,
  Clock3,
  MessageSquare,
  MessageCircle,
  Briefcase,
  Star,
  CheckCircle,
  Zap,
  Home,
  Wrench,
  Paintbrush,
  Truck,
  Laptop,
  ChefHat,
  Scissors,
  GraduationCap,
  PartyPopper,
  Building,
  Heart,
  Car,
  Dumbbell,
  Camera,
  Music,
  Shirt,
  Hammer,
  TreePine,
  Package,
  Settings,
  DollarSign,
  Calendar,
} from "lucide-react";
import howItWorksImage from "../assets/women_holding_phone.png";
import handymenImg from "../assets/handymen.png";
import housecleaningImg from "../assets/housecleaning.png";
import movingImg from "../assets/moving.png";
import painterImg from "../assets/painter.png";
import plumberImg from "../assets/plumber.png";
import electricianImg from "../assets/electrician.png";

/* ===================== THEME (matches other pages) ===================== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

/* ===================== GLOBAL & ANIMS ===================== */
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideInUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background};
    font-family: ${theme.font};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

/* ===================== LAYOUT ===================== */
const Page = styled.div`
  background: ${theme.colors.background};
  min-height: 100vh;
  padding-top: 64px;
  padding-left: 5px;
  padding-right: 5px;
  animation: ${fadeIn} 0.5s ease;
  overflow-x: hidden; // Prevents horizontal scroll from animations
`;

// Updated Hero section for centered content with organic background
const Hero = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1rem 4rem;
  animation: ${slideInUp} 0.6s ease-out;
  position: relative;
  overflow: hidden;
  background: transparent;

  &::before,
  &::after {
    display: none;
  }
`;

// Wrapper for the text content in the hero
const HeroTextWrapper = styled.div`
  max-width: 800px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const BackgroundIcons = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;

  .bg-icon {
    position: absolute;
    color: ${theme.colors.primary};
    opacity: 0.12;
    animation: float 8s ease-in-out infinite;
  }

  /* Orbital pattern around the center */
  .bg-icon:nth-child(1) {
    top: 8%;
    left: 12%;
    transform: rotate(15deg);
    animation-delay: 0s;
  }
  .bg-icon:nth-child(2) {
    top: 15%;
    right: 18%;
    transform: rotate(-20deg);
    animation-delay: 0.5s;
  }
  .bg-icon:nth-child(3) {
    top: 25%;
    left: 8%;
    transform: rotate(45deg);
    animation-delay: 1s;
  }
  .bg-icon:nth-child(4) {
    top: 35%;
    right: 8%;
    transform: rotate(-10deg);
    animation-delay: 1.5s;
  }
  .bg-icon:nth-child(5) {
    top: 45%;
    left: 15%;
    transform: rotate(30deg);
    animation-delay: 2s;
  }
  .bg-icon:nth-child(6) {
    top: 55%;
    right: 15%;
    transform: rotate(-45deg);
    animation-delay: 2.5s;
  }
  .bg-icon:nth-child(7) {
    top: 65%;
    left: 10%;
    transform: rotate(60deg);
    animation-delay: 3s;
  }
  .bg-icon:nth-child(8) {
    top: 75%;
    right: 12%;
    transform: rotate(-30deg);
    animation-delay: 3.5s;
  }
  .bg-icon:nth-child(9) {
    top: 85%;
    left: 20%;
    transform: rotate(10deg);
    animation-delay: 4s;
  }
  .bg-icon:nth-child(10) {
    top: 12%;
    left: 35%;
    transform: rotate(-15deg);
    animation-delay: 4.5s;
  }
  .bg-icon:nth-child(11) {
    top: 20%;
    right: 35%;
    transform: rotate(25deg);
    animation-delay: 5s;
  }
  .bg-icon:nth-child(12) {
    top: 30%;
    left: 25%;
    transform: rotate(-35deg);
    animation-delay: 5.5s;
  }
  .bg-icon:nth-child(13) {
    top: 40%;
    right: 25%;
    transform: rotate(50deg);
    animation-delay: 6s;
  }
  .bg-icon:nth-child(14) {
    top: 50%;
    left: 30%;
    transform: rotate(-25deg);
    animation-delay: 6.5s;
  }
  .bg-icon:nth-child(15) {
    top: 60%;
    right: 30%;
    transform: rotate(35deg);
    animation-delay: 7s;
  }
  .bg-icon:nth-child(16) {
    top: 70%;
    left: 25%;
    transform: rotate(-50deg);
    animation-delay: 7.5s;
  }
  .bg-icon:nth-child(17) {
    top: 80%;
    right: 25%;
    transform: rotate(20deg);
    animation-delay: 0.3s;
  }
  .bg-icon:nth-child(18) {
    top: 18%;
    left: 50%;
    transform: rotate(-40deg);
    animation-delay: 0.8s;
  }
  .bg-icon:nth-child(19) {
    top: 28%;
    right: 50%;
    transform: rotate(40deg);
    animation-delay: 1.3s;
  }
  .bg-icon:nth-child(20) {
    top: 38%;
    left: 45%;
    transform: rotate(-20deg);
    animation-delay: 1.8s;
  }
  .bg-icon:nth-child(21) {
    top: 48%;
    right: 45%;
    transform: rotate(55deg);
    animation-delay: 2.3s;
  }
  .bg-icon:nth-child(22) {
    top: 58%;
    left: 40%;
    transform: rotate(-60deg);
    animation-delay: 2.8s;
  }
  .bg-icon:nth-child(23) {
    top: 68%;
    right: 40%;
    transform: rotate(30deg);
    animation-delay: 3.3s;
  }

  @media (max-width: 768px) {
    .bg-icon:nth-child(n + 9) {
      display: none;
    }
    .bg-icon {
      opacity: 0.08;
    }
  }
`;

const Title = styled.h1`
  font-size: clamp(2rem, 6vw, 4rem);
  font-weight: 800;
  color: ${theme.colors.textHeading};
  margin: 0;
  letter-spacing: -0.04em;
  line-height: 1.1;

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const Subtitle = styled.p`
  max-width: 680px;
  margin: 1.75rem 0 0;
  font-size: 1.25rem;
  color: ${theme.colors.textMuted};
  line-height: 1.7;
  font-weight: 400;

  @media (max-width: 992px) {
    margin: 1.25rem auto 0;
    font-size: 1.125rem;
  }
`;

const TrustIndicators = styled.div`
  display: flex;
  gap: 32px;
  margin: 24px 0 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 20px;
    justify-content: center;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #059669;
  font-size: 0.9rem;
  font-weight: 600;

  svg {
    color: #059669;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const CategoriesSection = styled.div`
  text-align: center;
  margin: 48px 0;

  @media (max-width: 768px) {
    margin: 32px 0;
  }
`;

const CategoriesTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 24px;
`;

const Highlight = styled.span`
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: 6px;
  padding: 2px 6px;
  display: inline-block;
`;

// 🎨 NEW: Wrapper for the SVG Illustration
const HeroIllustrationWrapper = styled.div`
  flex: 0 0 45%;
  max-width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: auto;
  }

  // Hide the illustration on smaller screens to save space
  @media (max-width: 992px) {
    display: none;
  }
`;

// 🎨 NEW: SVG Illustration Component
const HeroIllustration = () => (
  <HeroIllustrationWrapper>
    <svg viewBox="0 0 480 320" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ropeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#facc15" />
        </linearGradient>
      </defs>
      {/* The large letter 'D' */}
      <path
        d="M140 10H280C346.274 10 400 63.7258 400 130V190C400 256.274 346.274 310 280 310H140V10Z"
        fill="#FFFFFF"
        stroke={theme.colors.border}
        strokeWidth="2"
      />
      <path
        d="M204 74H274C305.941 74 332 100.059 332 132V188C332 219.941 305.941 246 274 246H204V74Z"
        fill={theme.colors.background}
      />
      {/* The ladder */}
      <line
        x1="100"
        y1="310"
        x2="180"
        y2="110"
        stroke={theme.colors.primary}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <line
        x1="120"
        y1="310"
        x2="200"
        y2="110"
        stroke={theme.colors.primary}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <line
        x1="105"
        y1="280"
        x2="125"
        y2="280"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="125"
        y1="230"
        x2="145"
        y2="230"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="145"
        y1="180"
        x2="165"
        y2="180"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <line
        x1="165"
        y1="130"
        x2="185"
        y2="130"
        stroke={theme.colors.primary}
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* The rope */}
      <path d="M440 0V220" stroke="url(#ropeGradient)" strokeWidth="6" />
      {/* The character */}
      <g transform="translate(410 240)">
        <rect width="60" height="50" rx="10" fill={theme.colors.primary} />
        <circle cx="30" cy="-20" r="20" fill="#fde047" />
        <circle cx="22" cy="-24" r="3" fill="black" />
        <circle cx="38" cy="-24" r="3" fill="black" />
      </g>
    </svg>
  </HeroIllustrationWrapper>
);

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0;
  margin-bottom: 48px;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 320px;
    gap: 12px;
  }
`;

const QuickActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: ${(props) => (props.secondary ? "1px solid #e2e8f0" : "1px solid #3b82f6")};
  background: ${(props) =>
    props.secondary
      ? "#ffffff"
      : "#3b82f6"};
  color: ${(props) => (props.secondary ? "#334155" : "#ffffff")};
  box-shadow: none;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${(props) => (props.secondary ? theme.colors.primaryLight : "#2563eb")};
    border-color: ${theme.colors.primary};
    color: ${(props) => (props.secondary ? theme.colors.primary : "#ffffff")};
  }

  &:active {
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 16px 28px;
    justify-content: center;
    font-size: 1rem;
  }
`;

/* ===================== SEARCH ===================== */
const SearchWrapper = styled.form`
  width: 100%;
  max-width: 640px;
  display: flex;
  border-radius: 9999px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  transition: border-color 0.2s ease;
  box-shadow: none;

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &:hover {
    box-shadow: none;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${theme.colors.textHeading};
  min-width: 0;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.75rem;
  border: none;
  border-radius: 0 9999px 9999px 0;
  background: #3b82f6;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  box-shadow: none;

  &:hover {
    background: #2563eb;
  }
`;

/* ===================== OTHER SECTIONS (Unchanged) ===================== */
// ... (All other styled components like Section, SectionTitle, CardGrid, etc. remain the same)
const Section = styled.section`
  padding: 2rem 0 0;

  @media (max-width: 768px) {
    padding: 1rem 0 0;
  }
`;

const MobileSection = styled(Section)`
  margin-top: 48px;

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const TaskerSectionTitle = styled.h2`
  color: ${theme.colors.textHeading};
  font-size: 2.1rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSub = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 2.5rem;
  color: ${theme.colors.textMuted};
  font-size: 1.1rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    margin: 0 auto 2rem;
    font-size: 1rem;
  }
`;

const TaskerSectionSub = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1.05rem;
  margin: 0 0 12px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const PopularCategoriesSection = styled(Section)`
  margin-top: 64px;
  background: transparent;
  padding: 0;

  @media (max-width: 768px) {
    margin-top: 40px;
  }
`;

/* ===================== TASKER/EARN MONEY SECTION ===================== */
const TaskerSection = styled.section`
  margin-top: 100px;
  padding: 64px 48px;
  background: ${theme.colors.surface};
  border-radius: 20px;
  border: 1px solid ${theme.colors.border};
  position: relative;
  overflow: hidden;

  &::before,
  &::after {
    display: none;
  }

  @media (max-width: 768px) {
    margin-top: 72px;
    padding: 48px 24px;
    border-radius: 24px;
  }
`;

const TaskerLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const TaskerContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const TaskerImageWrapper = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  aspect-ratio: 4 / 3;

  @media (max-width: 900px) {
    order: -1;
    max-width: 400px;
    margin: 0 auto;
    width: 100%;
  }
`;

const TaskerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const TaskerFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 32px 0 40px;
`;

const TaskerFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const TaskerFeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TaskerFeatureTextWrap = styled.div`
  flex: 1;
`;

const TaskerFeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  margin: 0 0 6px;
  line-height: 1.3;
`;

const TaskerFeatureDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.5;
`;

const TaskerButton = styled.button`
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid ${theme.colors.primary};
  background: ${theme.colors.primary};
  color: #ffffff;
  position: relative;
  overflow: hidden;
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryText};
    border-color: ${theme.colors.primaryText};
  }

  &:active {
    transform: none;
    transition: none;
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 1rem;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
  margin-top: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-top: 32px;
  }
`;

const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: #f1f5f9;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  margin-bottom: 14px;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
    margin-bottom: 10px;
    border-radius: 14px;
  }

  svg {
    transition: none;
  }
`;

const CategoryCard = styled.button`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 14px;
  padding: 22px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  box-shadow: none;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};

    ${CategoryIcon} {
      background: #ffffff;
      border-color: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
    border-radius: 16px;
  }
`;

const CategoryName = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.textHeading};
  line-height: 1.4;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

/* ===================== IMAGE-BASED CATEGORY CARDS ===================== */
const ImageCategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-top: 28px;
  max-width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-top: 20px;
  }
`;

const ImageCategoryCard = styled.button`
  background: transparent;
  border: none;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  aspect-ratio: 4 / 5;
  transition: all 0.3s ease;

  &:hover {
    transform: none;
    box-shadow: none;

    img {
      transform: none;
    }
  }

  @media (max-width: 600px) {
    border-radius: 8px;
  }
`;

const CategoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
`;

const CategoryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 14px;
  background: rgba(15, 23, 42, 0.62);
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const CategoryLabel = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.32);
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 0.76rem;
    font-weight: 500;
  }
`;

const ViewAllCard = styled.button`
  background: #ffffff;
  border: 1px dashed ${theme.colors.border};
  border-radius: 14px;
  padding: 22px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 130px;
  color: ${theme.colors.textMuted};

  &:hover {
    border-color: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }

  svg {
    margin-bottom: 4px;
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover svg {
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
    min-height: 110px;
    border-radius: 16px;
  }
`;

const ViewAllText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const LoggedInSection = styled.section`
  text-align: center;
  padding: 4rem 1rem 3rem;
  margin-top: 48px;

  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const LoggedInTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 1rem;
  letter-spacing: -0.04em;
  line-height: 1.15;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const LoggedInSubtitle = styled.p`
  font-size: 1.125rem;
  color: ${theme.colors.textMuted};
  margin: 0 0 2rem;
  line-height: 1.6;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 20px;
  }
`;

const FeatureHighlight = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  box-shadow: none;
  display: flex;
  align-items: flex-start;
  gap: 18px;
`;

const HighlightIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: none;
`;

const HighlightContent = styled.div`
  flex: 1;
`;

const HighlightTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  display: inline-block;
`;

const HighlightText = styled.p`
  margin: 0 0 16px;
  color: ${theme.colors.textBody};
  line-height: 1.6;
`;

const HighlightLink = styled.button`
  background: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  padding: 8px 14px;
  border-radius: 8px;
  transition: background 0.2s ease;
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryText};
  }
`;

const FeatureGrid2 = styled.div`
  display: grid;
  gap: 18px;
`;

const CompactFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 16px;
  box-shadow: none;
`;

const CompactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: none;
`;

const CompactTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
`;

const CompactText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const SuccessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const SuccessCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  box-shadow: none;
`;

const SuccessQuote = styled.p`
  margin: 0 0 20px;
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.colors.textBody};
  font-style: italic;
`;

const SuccessAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SuccessAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SuccessName = styled.div`
  font-weight: 700;
  color: ${theme.colors.textHeading};
  font-size: 0.95rem;
`;

const SuccessStats = styled.div`
  margin-left: auto;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
`;

/* ===================== BLOCKED STATUS BANNER ===================== */
const BlockedBanner = styled.div`
  background: #fff7f7;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 14px 16px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: none;

  svg {
    color: #dc2626;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
    margin: 12px 0;
  }
`;

const BlockedContent = styled.div`
  flex: 1;
`;

const BlockedTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 700;
  color: #dc2626;
`;

const BlockedText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #7f1d1d;
  line-height: 1.5;
`;

const CTASection = styled.section`
  margin-top: 48px;

  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const CTACard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 12px;
  padding: 36px 24px;
  text-align: center;
  border: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 12px;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 12px;
  }
`;

const CTAText = styled.p`
  margin: 0 0 32px;
  font-size: 1rem;
  color: ${theme.colors.textMuted};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 24px;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryBtn = styled.button`
  background: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 12px 22px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryText};
  }

  @media (max-width: 480px) {
    width: 200px;
    justify-content: center;
  }
`;

const SecondaryBtn = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  color: ${theme.colors.textHeading};
  font-weight: 600;
  font-size: 1rem;
  padding: 11px 22px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  @media (max-width: 480px) {
    width: 200px;
    justify-content: center;
  }
`;

const CTANote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${theme.colors.textMuted};
  font-weight: 600;
`;

const PostTaskCTASection = styled.section`
  margin-top: 100px;
  margin-bottom: 100px;
  padding: 0;

  @media (max-width: 768px) {
    margin-top: 72px;
    margin-bottom: 72px;
  }
`;

const PostTaskCTAContent = styled.div`
  background: transparent;
  padding: 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PostTaskCTATitle = styled.h2`
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.textHeading};
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const PostTaskCTAText = styled.p`
  margin: 0 0 32px;
  font-size: 1.1rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;

  @media (max-width: 768px) {
    margin-bottom: 24px;
    font-size: 1rem;
  }
`;

const SearchFormWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

/* ===================== FOOTER ===================== */
const Footer = styled.footer`
  background: #f8fafc;
  border-top: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  padding: 48px 0 24px;

  @media (max-width: 768px) {
    padding: 32px 0 16px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: center;
    margin-bottom: 20px;
  }
`;

const FooterBrand = styled.div`
  h3 {
    color: ${theme.colors.textHeading};
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 12px;
  }

  p {
    line-height: 1.6;
    margin: 0 0 20px;
    max-width: 300px;

    @media (max-width: 768px) {
      margin: 0 auto 20px;
    }
  }
`;

const FooterSection = styled.div`
  h4 {
    color: ${theme.colors.textHeading};
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 16px;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 8px;

  a {
    color: ${theme.colors.textMuted};
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    padding-top: 16px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;

  a {
    color: ${theme.colors.textMuted};
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

/* ===================== HOW IT WORKS SECTION ===================== */
const HowItWorksSection = styled.div`
  background: transparent;
  padding: 0;
  margin-top: 100px;
  position: relative;

  @media (max-width: 768px) {
    margin-top: 72px;
  }
`;

const HowItWorksLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const HowItWorksContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HowItWorksTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 32px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 24px;
  }
`;

const HowItWorksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const HowItWorksImageWrapper = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  aspect-ratio: 4 / 3;

  @media (max-width: 900px) {
    order: -1;
    max-width: 500px;
    margin: 0 auto;
    width: 100%;
  }
`;

const HowItWorksImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HowItWorksStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(props) => props.color || "#e0e7ff"};
  color: ${(props) => props.textColor || theme.colors.textHeading};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
`;

const StepTextWrap = styled.div`
  flex: 1;
  padding-top: 10px;
`;

const StepDescription = styled.p`
  font-size: 1.15rem;
  color: ${theme.colors.textHeading};
  margin: 0;
  line-height: 1.6;
  font-family: Sans-serif;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

/* ===================== GET HELP TODAY SECTION ===================== */
const GetHelpSection = styled.section`
  margin-top: 100px;
  padding: 0;

  @media (max-width: 768px) {
    margin-top: 72px;
  }
`;

const GetHelpTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 32px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 24px;
  }
`;

const ServiceTagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

const ServiceTag = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: 9999px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${theme.colors.textBody};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const SeeAllLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  transition: gap 0.2s ease;

  &:hover {
    gap: 10px;
  }
`;

const HowItWorksCTA = styled.div`
  text-align: center;
  margin-top: 48px;

  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const CTAButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: 1px solid ${theme.colors.primary};
  border-radius: 10px;
  padding: 14px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${theme.colors.primaryText};
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1rem;
  }
`;

/* ===================== PAGE ===================== */
const HomePage = ({ userState }) => {
  const navigate = useNavigate();
  const topInputRef = useRef(null);
  const bottomInputRef = useRef(null);
  const [allCategories, setAllCategories] = useState([]); // All categories for navigation

  // Fetch categories for navigation lookup
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await request(
          "POST",
          API_CONSTANT_MAP.fetch_categories,
        );
        if (response?.data?.success) {
          setAllCategories(response.data.categories || []);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Lambda warming effect
  useEffect(() => {
    const warmLambdas = async () => {
      // Critical endpoints to warm - these are the most commonly used
      const criticalEndpoints = [
        { url: API_CONSTANT_MAP.fetch_tasks, method: "POST", data: {} },
        {
          url: API_CONSTANT_MAP.fetch_task_time_windows,
          method: "POST",
          data: {},
        },
        { url: API_CONSTANT_MAP.signin, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.fetch_task_by_id, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.create_task_offer, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.create_task, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.fetch_posted_tasks, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.list_conversations, method: "POST", data: {} },
      ];

      // Fire warming requests in parallel, ignore errors
      const warmingPromises = criticalEndpoints.map(async (endpoint) => {
        try {
          await request(endpoint.method, endpoint.url, endpoint.data);
        } catch (error) {
          // Silently ignore errors - we just want to warm the Lambdas
          console.debug(`Lambda warming for ${endpoint.url}: OK`);
        }
      });

      // Don't await - let them run in background
      Promise.allSettled(warmingPromises);
    };

    // Warm Lambdas in background without blocking render
    warmLambdas();
  }, []);

  // Check for onboarding redirect (but not if they came from account page)
  useEffect(() => {
    if (
      userState?.user?.token &&
      userState.user.onboarding_status === "pending"
    ) {
      // Don't redirect if user is trying to access account page
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith("/account")) {
        navigate("/onboarding");
      }
    }
  }, [userState?.user?.onboarding_status, userState?.user?.token, navigate]);

  // Helper function to find category by name keyword and navigate
  const navigateToCategoryByName = useCallback(
    (keyword) => {
      const category = allCategories.find((cat) =>
        cat.name.toLowerCase().includes(keyword.toLowerCase()),
      );
      if (category) {
        navigate(`/tasks/?category=${category.id}`);
      } else {
        // Fallback to search if category not found
        navigate(`/tasks?search=${keyword}`);
      }
    },
    [allCategories, navigate],
  );

  // Helper function to get category icon
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();

    // Cleaning & Home
    if (
      name.includes("clean") ||
      name.includes("house") ||
      name.includes("domestic")
    )
      return <Home size={20} />;

    // Garden & Landscaping
    if (
      name.includes("garden") ||
      name.includes("yard") ||
      name.includes("lawn") ||
      name.includes("landscape")
    )
      return <TreePine size={20} />;

    // Repair & Maintenance
    if (
      name.includes("repair") ||
      name.includes("fix") ||
      name.includes("maintenance") ||
      name.includes("handyman")
    )
      return <Wrench size={20} />;

    // Moving & Delivery
    if (
      name.includes("move") ||
      name.includes("transport") ||
      name.includes("delivery") ||
      name.includes("shipping")
    )
      return <Truck size={20} />;

    // Painting & Design
    if (
      name.includes("paint") ||
      name.includes("design") ||
      name.includes("decor") ||
      name.includes("art")
    )
      return <Paintbrush size={20} />;

    // Pet Services
    if (
      name.includes("pet") ||
      name.includes("dog") ||
      name.includes("cat") ||
      name.includes("animal")
    )
      return <Heart size={20} />;

    // Technology & IT
    if (
      name.includes("tech") ||
      name.includes("computer") ||
      name.includes("digital") ||
      name.includes("web") ||
      name.includes("software")
    )
      return <Laptop size={20} />;

    // Food & Cooking
    if (
      name.includes("food") ||
      name.includes("cook") ||
      name.includes("grocery") ||
      name.includes("catering") ||
      name.includes("meal")
    )
      return <ChefHat size={20} />;

    // Beauty & Personal Care
    if (
      name.includes("beauty") ||
      name.includes("hair") ||
      name.includes("makeup") ||
      name.includes("nail") ||
      name.includes("spa")
    )
      return <Scissors size={20} />;

    // Education & Tutoring
    if (
      name.includes("tutor") ||
      name.includes("lesson") ||
      name.includes("teach") ||
      name.includes("education") ||
      name.includes("training")
    )
      return <GraduationCap size={20} />;

    // Events & Entertainment
    if (
      name.includes("event") ||
      name.includes("party") ||
      name.includes("wedding") ||
      name.includes("entertainment")
    )
      return <PartyPopper size={20} />;

    // Business & Office
    if (
      name.includes("business") ||
      name.includes("office") ||
      name.includes("admin") ||
      name.includes("accounting")
    )
      return <Building size={20} />;

    // Construction & Building & Carpentry (check before Automotive since 'carpentry' contains 'car')
    if (
      name.includes("construction") ||
      name.includes("carpentry") ||
      name.includes("contractor") ||
      name.includes("woodwork")
    )
      return <Hammer size={20} />;

    // Caregiving Services (check before Automotive since 'caregiving' contains 'car')
    if (
      name.includes("caregiving") ||
      name.includes("elderly") ||
      name.includes("senior") ||
      name.includes("nursing")
    )
      return <Heart size={20} />;

    // Automotive
    if (
      name.includes("car") ||
      name.includes("auto") ||
      name.includes("vehicle") ||
      name.includes("mechanic")
    )
      return <Car size={20} />;

    // Fitness & Health
    if (
      name.includes("fitness") ||
      name.includes("gym") ||
      name.includes("workout") ||
      name.includes("health")
    )
      return <Dumbbell size={20} />;

    // Photography & Media
    if (
      name.includes("photo") ||
      name.includes("video") ||
      name.includes("media") ||
      name.includes("camera")
    )
      return <Camera size={20} />;

    // Music & Audio
    if (
      name.includes("music") ||
      name.includes("audio") ||
      name.includes("sound") ||
      name.includes("instrument")
    )
      return <Music size={20} />;

    // Fashion & Clothing
    if (
      name.includes("fashion") ||
      name.includes("clothing") ||
      name.includes("tailor") ||
      name.includes("sewing")
    )
      return <Shirt size={20} />;

    // Assembly & Installation
    if (
      name.includes("assembly") ||
      name.includes("install") ||
      name.includes("setup") ||
      name.includes("furniture")
    )
      return <Package size={20} />;

    // Default for any other categories
    return <Settings size={20} />;
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar />
        <OuterContainer>
          {/* Show blocked status banner if user is blocked */}
          {userState?.user?.token && userState.user.is_blocked && (
            <BlockedBanner>
              <Shield size={24} />
              <BlockedContent>
                <BlockedTitle>Account Suspended</BlockedTitle>
                <BlockedText>
                  Your account has been suspended. Contact support if you
                  believe this is an error.
                </BlockedText>
              </BlockedContent>
            </BlockedBanner>
          )}

          {/* ✅ REDESIGNED HERO SECTION */}
          <Hero>
            <BackgroundIcons>
              <Settings className="bg-icon" size={24} />
              <Package className="bg-icon" size={26} />
              <Home className="bg-icon" size={28} />
              <TreePine className="bg-icon" size={22} />
              <Truck className="bg-icon" size={30} />
              <Wrench className="bg-icon" size={24} />
              <Scissors className="bg-icon" size={26} />
              <Hammer className="bg-icon" size={28} />
              <Paintbrush className="bg-icon" size={22} />
              <Zap className="bg-icon" size={24} />
              <TreePine className="bg-icon" size={26} />
              <Settings className="bg-icon" size={28} />
              <Home className="bg-icon" size={22} />
              <Heart className="bg-icon" size={24} />
              <Truck className="bg-icon" size={26} />
              <Laptop className="bg-icon" size={28} />
              <GraduationCap className="bg-icon" size={22} />
              <PartyPopper className="bg-icon" size={24} />
              <Camera className="bg-icon" size={26} />
              <Shirt className="bg-icon" size={28} />
              <Paintbrush className="bg-icon" size={22} />
              <Building className="bg-icon" size={24} />
              <Settings className="bg-icon" size={26} />
            </BackgroundIcons>
            <HeroTextWrapper>
              <Title>
                Turn your <Highlight>to-do list</Highlight> <br />
                into done
              </Title>
              <Subtitle>
                Hire local help for any task. Post for free, get offers, and get
                it done.
              </Subtitle>
            </HeroTextWrapper>
            {/* <HeroIllustration /> */}
          </Hero>

          <ContentWrapper>
            <QuickActions>
              <QuickActionButton onClick={() => navigate("/posttask")}>
                Post your request
              </QuickActionButton>
              {userState?.user?.token && (
                <QuickActionButton onClick={() => navigate("/tasks")} secondary>
                  Browse available tasks
                </QuickActionButton>
              )}
            </QuickActions>
          </ContentWrapper>

          {/* Popular Services - Image-based cards */}
          <PopularCategoriesSection>
            <SectionTitle>Popular categories</SectionTitle>
            <ImageCategoriesGrid>
              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("Handyman Services")}
              >
                <CategoryImage src={handymenImg} alt="Handyman Services" />
                <CategoryOverlay>
                  <CategoryLabel>Handyman Services</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>

              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("House Cleaning")}
              >
                <CategoryImage src={housecleaningImg} alt="House Cleaning" />
                <CategoryOverlay>
                  <CategoryLabel>House Cleaning</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>

              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("Moving Help")}
              >
                <CategoryImage src={movingImg} alt="Moving Help" />
                <CategoryOverlay>
                  <CategoryLabel>Moving Help</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>

              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("Painting")}
              >
                <CategoryImage src={painterImg} alt="Painting" />
                <CategoryOverlay>
                  <CategoryLabel>Painting</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>

              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("Plumbing")}
              >
                <CategoryImage src={plumberImg} alt="Plumbing" />
                <CategoryOverlay>
                  <CategoryLabel>Plumbing</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>

              <ImageCategoryCard
                onClick={() => navigateToCategoryByName("Electrical Work")}
              >
                <CategoryImage src={electricianImg} alt="Electrical Work" />
                <CategoryOverlay>
                  <CategoryLabel>Electrical Work</CategoryLabel>
                </CategoryOverlay>
              </ImageCategoryCard>
            </ImageCategoriesGrid>
          </PopularCategoriesSection>

          {/* How it Works - TaskRabbit style with numbered circles and image */}
          {!userState?.user?.token && (
            <HowItWorksSection>
              <HowItWorksLayout>
                <HowItWorksContent>
                  <HowItWorksTitle>How it works</HowItWorksTitle>

                  <HowItWorksContainer>
                    <HowItWorksStep>
                      <StepNumber color="#e0e7ff" textColor="#3b82f6">
                        1
                      </StepNumber>
                      <StepTextWrap>
                        <StepDescription>
                          <strong>Post your task</strong> and describe what you
                          need and your <strong>budget</strong>.
                        </StepDescription>
                      </StepTextWrap>
                    </HowItWorksStep>

                    <HowItWorksStep>
                      <StepNumber color="#fef3c7" textColor="#92400e">
                        2
                      </StepNumber>
                      <StepTextWrap>
                        <StepDescription>
                          <strong>Receive offers</strong> from local helpers.
                        </StepDescription>
                      </StepTextWrap>
                    </HowItWorksStep>

                    <HowItWorksStep>
                      <StepNumber color="#d1fae5" textColor="#065f46">
                        3
                      </StepNumber>
                      <StepTextWrap>
                        <StepDescription>
                          <strong>Get it done</strong> with your chosen helper.
                        </StepDescription>
                      </StepTextWrap>
                    </HowItWorksStep>
                  </HowItWorksContainer>
                </HowItWorksContent>

                <HowItWorksImageWrapper>
                  <HowItWorksImage
                    src={howItWorksImage}
                    alt="Woman using phone to hire help"
                  />
                </HowItWorksImageWrapper>
              </HowItWorksLayout>
            </HowItWorksSection>
          )}

          {/* Logged-in user: Simple post task flow like Airtasker */}
          {userState?.user?.token && (
            <LoggedInSection>
              <LoggedInTitle>What do you need done?</LoggedInTitle>
              <LoggedInSubtitle>
                Tell us what you need and connect with local helpers
              </LoggedInSubtitle>
              <ContentWrapper>
                <SearchWrapper
                  onSubmit={(e) => {
                    e.preventDefault();
                    const taskDescription =
                      bottomInputRef.current?.value.trim();
                    if (taskDescription) {
                      navigate(
                        `/posttask?description=${encodeURIComponent(taskDescription)}`,
                      );
                    } else {
                      navigate("/posttask");
                    }
                  }}
                >
                  <SearchInput
                    ref={bottomInputRef}
                    type="text"
                    placeholder="What do you need help with?"
                    autoComplete="off"
                  />
                  <SearchButton type="submit" aria-label="Find Help">
                    Find Help
                  </SearchButton>
                </SearchWrapper>
              </ContentWrapper>
            </LoggedInSection>
          )}

          {/* Become a Tasker section - MOVED UP for priority */}
          {!userState?.user?.token && (
            <TaskerSection>
              <TaskerLayout>
                <TaskerContent>
                  <TaskerSectionTitle>
                    Earn money on your own terms
                  </TaskerSectionTitle>
                  <TaskerSectionSub>
                    Help others in your community and get paid for your skills.
                  </TaskerSectionSub>

                  <TaskerFeatures>
                    <TaskerFeature>
                      <TaskerFeatureIcon>
                        <Clock3 size={22} />
                      </TaskerFeatureIcon>
                      <TaskerFeatureTextWrap>
                        <TaskerFeatureTitle>
                          Work whenever you want
                        </TaskerFeatureTitle>
                        <TaskerFeatureDescription>
                          Pick tasks that fit your life and your location.
                        </TaskerFeatureDescription>
                      </TaskerFeatureTextWrap>
                    </TaskerFeature>

                    <TaskerFeature>
                      <TaskerFeatureIcon>
                        <DollarSign size={22} />
                      </TaskerFeatureIcon>
                      <TaskerFeatureTextWrap>
                        <TaskerFeatureTitle>
                          You set the price
                        </TaskerFeatureTitle>
                        <TaskerFeatureDescription>
                          You're the boss—you decide what your time is worth.
                        </TaskerFeatureDescription>
                      </TaskerFeatureTextWrap>
                    </TaskerFeature>

                    <TaskerFeature>
                      <TaskerFeatureIcon>
                        <Star size={22} />
                      </TaskerFeatureIcon>
                      <TaskerFeatureTextWrap>
                        <TaskerFeatureTitle>
                          Grow your side hustle
                        </TaskerFeatureTitle>
                        <TaskerFeatureDescription>
                          Build a top-rated profile to earn more and land better
                          jobs.
                        </TaskerFeatureDescription>
                      </TaskerFeatureTextWrap>
                    </TaskerFeature>
                  </TaskerFeatures>

                  <TaskerButton onClick={() => navigate("/signup")}>
                    Join as a Tasker
                  </TaskerButton>
                </TaskerContent>

                <TaskerImageWrapper>
                  <TaskerImage
                    src={handymenImg}
                    alt="Tasker helping with a project"
                  />
                </TaskerImageWrapper>
              </TaskerLayout>
            </TaskerSection>
          )}

          {/* Post Task CTA Section */}
          {!userState?.user?.token && (
            <PostTaskCTASection>
              <PostTaskCTAContent>
                <PostTaskCTATitle>Ready to get started?</PostTaskCTATitle>
                <PostTaskCTAText>
                  Describe what you need help with
                </PostTaskCTAText>
                <SearchFormWrapper>
                  <SearchWrapper
                    onSubmit={(e) => {
                      e.preventDefault();
                      const taskDescription = topInputRef.current?.value.trim();
                      if (taskDescription) {
                        navigate(
                          `/posttask?description=${encodeURIComponent(taskDescription)}`,
                        );
                      } else {
                        navigate("/posttask");
                      }
                    }}
                  >
                    <SearchInput
                      ref={topInputRef}
                      type="text"
                      placeholder="What do you need help with?"
                      autoComplete="off"
                    />
                    <SearchButton type="submit" aria-label="Post Task">
                      Post Task
                    </SearchButton>
                  </SearchWrapper>
                </SearchFormWrapper>
              </PostTaskCTAContent>
            </PostTaskCTASection>
          )}
        </OuterContainer>

        {/* Footer */}
        <Footer>
          <OuterContainer>
            <FooterContent>
              <FooterBrand>
                <h3>Doitly</h3>
                <p>
                  The marketplace connecting task posters with people ready to
                  help. Get things done or earn money with your skills.
                </p>
              </FooterBrand>

              <FooterSection>
                <h4>For Task Posters</h4>
                <FooterList>
                  <FooterLink>
                    <a onClick={() => navigate("/posttask")}>Need Help?</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/tasks")}>Find Work</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/howitworks")}>How It Works</a>
                  </FooterLink>
                  <FooterLink>
                    <a onClick={() => navigate("/safety")}>Safety Tips</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <h4>For Task Helpers</h4>
                <FooterList>
                  <FooterLink>
                    <a onClick={() => navigate("/tasks")}>Browse Tasks</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Submit Proposals</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Success Tips</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Profile Guide</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <h4>Support</h4>
                <FooterList>
                  <FooterLink>
                    <a>Help Center</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Contact Us</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Community Guidelines</a>
                  </FooterLink>
                  <FooterLink>
                    <a>Trust & Safety</a>
                  </FooterLink>
                </FooterList>
              </FooterSection>
            </FooterContent>

            <FooterBottom>
              <div>
                © {new Date().getFullYear()} Doitly Inc. All rights reserved. |
                Privacy Policy | Terms of Service
              </div>
              <SocialLinks>
                <a>Twitter</a>
                <a>LinkedIn</a>
                <a>Facebook</a>
              </SocialLinks>
            </FooterBottom>
          </OuterContainer>
        </Footer>
      </Page>
    </>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(HomePage);
